import {
    ActionIcon,
    Group,
    Modal,
    Paper,
    Skeleton,
    Table
} from '@mantine/core';
import {
    AiOutlineInfoCircle,
    AiOutlineLink
} from 'react-icons/ai';
import {useDisclosure} from '@mantine/hooks';
import {useState} from "react";

// FNP-960: the shape this component actually reads off the ipwho.is response.
// Fields are optional because the API returns `{success: false}` for a lookup
// it cannot resolve, and the render already guards each one.
type IpInfo = {
    ip?: string,
    city?: string,
    region?: string,
    country?: string,
    continent?: string,
    timezone?: { id?: string },
    latitude?: string,
    longitude?: string,
};

const GEOLOCATION_API_ENDPOINT = 'https://ipwho.is'
const MAP_API_ENDPOINT = 'https://www.google.com/maps/search'

const MapActionIcon = (props: { ipInfo: { latitude?: string, longitude?: string } }) => {

    const {ipInfo} = props

    return (
        <ActionIcon
            title={'See Coordinates Map'}
            variant={'subtle'}
            onClick={() => window.open(`${MAP_API_ENDPOINT}/?api=1&query=${ipInfo.latitude},${ipInfo.longitude}`)}>
            <AiOutlineLink/>
        </ActionIcon>
    )
}

export const IpInfoModal = (props: { ipAddress: string }) => {

    const {ipAddress} = props

    // FNP-960: the `ipAddress === undefined` guard used to sit ABOVE these
    // hooks, so the component called two fewer hooks on that path. React
    // requires an identical hook sequence every render — when ipAddress went
    // from defined to undefined (or back) this produced "Rendered fewer hooks
    // than expected" and unmounted the tree. This is a real runtime bug, not a
    // lint nit; the guard now runs after the hooks, which is the standard fix.
    const [opened, {
        open,
        close
    }] = useDisclosure(false);

    const [ipInfo, setIpInfo] = useState<IpInfo | null>(null)

    // Guard moved BELOW the hooks — see the note above. Same behaviour for the
    // caller (nothing renders without an ipAddress), but the hook sequence is
    // now identical on every render.
    if (typeof ipAddress === 'undefined') {
        return null
    }

    const handleOpen = () => {

        setIpInfo(null)

        fetch(`${GEOLOCATION_API_ENDPOINT}/${ipAddress}`)
            .then(response => response.json())
            .then(data => {
                setIpInfo(data)
            })
            .catch(error => {
                console.error(error)
            });

        open()
    }

    const tableData = [
        {
            label: 'IP Address',
            value: ipInfo ? ipInfo.ip : <Skeleton height={10} radius="sm"/>
        },
        {
            label: 'City',
            value: ipInfo ? ipInfo.city : <Skeleton height={10} radius="sm"/>
        },
        {
            label: 'Country',
            value: ipInfo ? ipInfo.country : <Skeleton height={10} radius="sm"/>
        },
        {
            label: 'Region',
            value: ipInfo ? ipInfo.region : <Skeleton height={10} radius="sm"/>
        },
        {
            label: 'Continent',
            value: ipInfo ? ipInfo.continent : <Skeleton height={10} radius="sm"/>
        },
        {
            label: 'Lat / Long',
            value: ipInfo ? <Group>
                    {`${ipInfo.latitude} / ${ipInfo.longitude}`}
                    <MapActionIcon ipInfo={ipInfo}/>
                </Group> :
                <Skeleton height={10} radius="sm"/>
        },
        {
            label: 'Timezone',
            value: ipInfo ? ipInfo.timezone?.id : <Skeleton height={10} radius="sm"/>
        }
    ]

    return (
        <>
            <Modal opened={opened} onClose={close} title="IP Info" size={'lg'}>
                <Paper shadow={'sm'} withBorder>
                    <Table striped>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th w={200}>Label</Table.Th>
                                <Table.Th>Value</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {tableData.map((row) => (
                                <Table.Tr key={row.label}>
                                    <Table.Td>{row.label}</Table.Td>
                                    <Table.Td>{row.value}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Paper>
            </Modal>

            <ActionIcon variant="subtle" onClick={handleOpen} title={'IP Address Lookup'}>
                <AiOutlineInfoCircle/>
            </ActionIcon>
        </>
    )

};
